<template>
  <div id="device-metrics">
    <v-card class="mb-4">
      <v-card-text>
        <v-autocomplete
          v-model="manualDeviceIds"
          :items="availableDevices"
          item-text="device_id"
          item-value="device_id"
          label="Devices"
          multiple
          chips
          small-chips
          outlined
          dense
          hide-details
        />
      </v-card-text>
    </v-card>

    <v-card class="mb-4">
      <v-card-title class="d-flex flex-wrap align-center" style="gap: 16px">
        <span class="text-xl font-weight-semibold">Device Diagnostics</span>

        <v-spacer />

        <v-switch v-model="showReboots" label="Show device reboots" inset hide-details class="mt-0" />
      </v-card-title>

      <v-card-text>
        <v-row dense class="mb-2">
          <v-col cols="12" md="5">
            <v-text-field v-model="manualStart" type="datetime-local" label="Start" outlined dense hide-details />
          </v-col>
          <v-col cols="12" md="5">
            <v-text-field v-model="manualEnd" type="datetime-local" label="End" outlined dense hide-details />
          </v-col>
          <v-col cols="12" md="2" class="d-flex justify-end">
            <v-btn color="primary" :disabled="!canLoadDiagnostics" @click="loadDiagnostics"> Load </v-btn>
          </v-col>
        </v-row>

        <div class="d-flex align-center flex-wrap mb-2">
          <span class="text-caption me-2">Field:</span>
          <v-chip
            v-for="field in metricFields"
            :key="field.key"
            small
            class="mr-2 mb-1"
            color="primary"
            :outlined="field.key !== metric"
            :text-color="field.key === metric ? 'white' : 'primary'"
            @click="metric = field.key"
          >
            {{ field.label }}
          </v-chip>
        </div>

        <div v-if="loading" class="d-flex justify-center align-center py-12">
          <Loader />
        </div>
        <div v-else-if="!hasAnyData" class="text-center py-8 text--secondary">
          {{ emptyStateMessage }}
        </div>
        <vue-apex-charts v-else type="line" height="450" :options="chartOptions" :series="chartSeries" />
      </v-card-text>
    </v-card>

    <v-card>
      <v-card-title>
        <span class="text-xl font-weight-semibold">SSD Health</span>
      </v-card-title>

      <v-card-text>
        <v-row dense class="mb-2">
          <v-col cols="12" md="5">
            <v-text-field v-model="manualSsdStart" type="datetime-local" label="Start" outlined dense hide-details />
          </v-col>
          <v-col cols="12" md="5">
            <v-text-field v-model="manualSsdEnd" type="datetime-local" label="End" outlined dense hide-details />
          </v-col>
          <v-col cols="12" md="2" class="d-flex justify-end">
            <v-btn color="primary" :disabled="!canLoadSsd" @click="loadSsd"> Load </v-btn>
          </v-col>
        </v-row>

        <DeviceSsdHealthComparison
          :device-ids="ssdDeviceIds"
          :records-by-device="ssdByDevice"
          :loading="ssdLoading"
          :range-start="ssdRangeStart"
          :range-end="ssdRangeEnd"
          :empty-message="ssdEmptyStateMessage"
        />
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { mapState } from "vuex"
import axios from "axios"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import timezone from "dayjs/plugin/timezone"
import VueApexCharts from "vue-apexcharts"
import Loader from "@/components/Loader.vue"
import DeviceSsdHealthComparison from "./components/DeviceSsdHealthComparison.vue"
import store from "@/store"
import { getSeriesColor, getSeriesTextColor } from "@/utils/chartColors"

dayjs.extend(utc)
dayjs.extend(timezone)

const METRIC_FIELDS = [
  { key: "cpu_usage_percent", label: "CPU Usage (%)" },
  { key: "cpu_temp_celcius", label: "CPU Temp (°C)" },
  { key: "memory_usage_percent", label: "Memory Usage (%)" },
  { key: "disk_usage_percent", label: "Disk Usage (%)" },
]

const nowInput = () => dayjs.tz(new Date(), "Pacific/Auckland").format("YYYY-MM-DDTHH:mm")
const agoInput = (amount, unit) =>
  dayjs.tz(new Date(), "Pacific/Auckland").subtract(amount, unit).format("YYYY-MM-DDTHH:mm")

export default {
  name: "DeviceMetrics",
  components: {
    VueApexCharts,
    Loader,
    DeviceSsdHealthComparison,
  },
  data() {
    return {
      metricFields: METRIC_FIELDS,
      metric: "cpu_usage_percent",
      showReboots: false,
      noteMarkers: [],

      // Each card tracks what it actually has loaded, so re-querying one leaves
      // the other's chart standing
      loading: false,
      diagnosticsDeviceIds: [],
      diagnosticsStart: "",
      diagnosticsEnd: "",
      loadedDiagnosticsKey: "",

      ssdLoading: false,
      ssdDeviceIds: [],
      ssdStart: "",
      ssdEnd: "",
      loadedSsdKey: "",

      seriesByDevice: {},
      rebootsByDevice: {},
      ssdByDevice: {},
      metricsCancelTokens: {},
      rebootsCancelTokens: {},
      ssdCancelTokens: {},

      manualDeviceIds: [],
      manualStart: agoInput(1, "day"),
      manualEnd: nowInput(),
      // SSD snapshots arrive roughly daily, so its default reaches back far
      // enough for the trend to be worth plotting
      manualSsdStart: agoInput(30, "day"),
      manualSsdEnd: nowInput(),
    }
  },
  computed: {
    ...mapState({
      availableDevices: state => state.gtfs.devices || [],
    }),
    canLoadDiagnostics() {
      return this.manualDeviceIds.length > 0 && !!this.manualStart && !!this.manualEnd
    },
    canLoadSsd() {
      return this.manualDeviceIds.length > 0 && !!this.manualSsdStart && !!this.manualSsdEnd
    },
    rangeStart() {
      return this.diagnosticsStart ? dayjs.tz(this.diagnosticsStart, "Pacific/Auckland").valueOf() : null
    },
    rangeEnd() {
      return this.diagnosticsEnd ? dayjs.tz(this.diagnosticsEnd, "Pacific/Auckland").valueOf() : null
    },
    ssdRangeStart() {
      return this.ssdStart ? dayjs.tz(this.ssdStart, "Pacific/Auckland").valueOf() : null
    },
    ssdRangeEnd() {
      return this.ssdEnd ? dayjs.tz(this.ssdEnd, "Pacific/Auckland").valueOf() : null
    },
    hasAnyData() {
      return this.diagnosticsDeviceIds.some(id => (this.seriesByDevice[id] || []).length > 0)
    },
    emptyStateMessage() {
      if (!this.diagnosticsDeviceIds.length) {
        return "Select devices and a date range, then press Load to view diagnostics."
      }

      return "No diagnostics data for the selected range."
    },
    ssdEmptyStateMessage() {
      if (!this.ssdDeviceIds.length) {
        return "Select devices and a date range, then press Load to view SSD health."
      }

      return "No SSD health data for the selected range."
    },
    metricLabel() {
      return METRIC_FIELDS.find(field => field.key === this.metric)?.label
    },
    deviceColors() {
      return this.diagnosticsDeviceIds.map((_, i) => getSeriesColor(i))
    },
    chartSeries() {
      const GAP_MS = 10 * 60 * 1000

      return this.diagnosticsDeviceIds.map(id => {
        const points = this.seriesByDevice[id] || []
        const data = []
        points.forEach((p, i) => {
          const value = Number.isFinite(p[this.metric]) ? p[this.metric] : null
          const prev = points[i - 1]
          if (prev && p.timestamp - prev.timestamp > GAP_MS) {
            data.push([prev.timestamp + 1, null])
          }
          data.push([p.timestamp, value])
        })

        return { name: id, data }
      })
    },
    chartOptions() {
      const formatNZ = (value, format) => dayjs(value).tz("Pacific/Auckland").format(format)

      const colorConfigForDevice = id => {
        const idx = this.diagnosticsDeviceIds.indexOf(id)

        return idx === -1
          ? { color: "#FFB300", textColor: "#000" }
          : { color: this.deviceColors[idx], textColor: getSeriesTextColor(idx) }
      }

      const noteAnnotations = this.noteMarkers
        .filter(m => Number.isFinite(m.timestamp))
        .map(m => {
          const { color, textColor } = colorConfigForDevice(m.deviceId)

          return {
            x: m.timestamp,
            strokeDashArray: 4,
            borderColor: color,
            label: {
              text: `Note · ${m.deviceId} ${formatNZ(m.timestamp, "HH:mm")}`,
              borderColor: color,
              style: { color: textColor, background: color },
            },
          }
        })

      const rebootAnnotations = this.showReboots
        ? Object.entries(this.rebootsByDevice).flatMap(([deviceId, reboots]) => {
            const { color, textColor } = colorConfigForDevice(deviceId)

            return reboots
              .map(r => dayjs.tz(r.event_data.date_time, "Pacific/Auckland").valueOf())
              .filter(Number.isFinite)
              .map(timestamp => ({
                x: timestamp,
                strokeDashArray: 4,
                borderColor: color,
                label: {
                  text: `Reboot · ${deviceId} ${formatNZ(timestamp, "HH:mm")}`,
                  borderColor: color,
                  style: { color: textColor, background: color },
                },
              }))
          })
        : []

      return {
        chart: {
          type: "line",
          toolbar: { show: true },
          zoom: { enabled: true },
        },
        colors: this.deviceColors,
        stroke: { curve: "straight", width: 1 },
        markers: { size: 2, strokeWidth: 0 },
        xaxis: {
          type: "datetime",
          min: this.rangeStart ?? undefined,
          max: this.rangeEnd ?? undefined,
          labels: {
            datetimeUTC: false,
            formatter: value => formatNZ(value, "DD MMM HH:mm"),
          },
        },
        yaxis: {
          decimalsInFloat: 1,
          title: { text: this.metricLabel },
        },
        legend: { show: true, position: "top" },
        tooltip: {
          x: {
            formatter: value => formatNZ(value, "YYYY-MM-DD HH:mm"),
          },
        },
        annotations: {
          xaxis: [...noteAnnotations, ...rebootAnnotations],
        },
      }
    },
  },
  watch: {
    "$route.query": {
      handler() {
        this.applyQuery()
      },
      deep: true,
    },
    showReboots(val) {
      if (!val) return
      this.diagnosticsDeviceIds.forEach(id => {
        if (!this.rebootsByDevice[id]) {
          this.fetchReboots(id)
        }
      })
    },
  },
  created() {
    this.applyQuery()
  },
  beforeDestroy() {
    Object.values(this.metricsCancelTokens).forEach(s => s?.cancel?.("Component destroyed"))
    Object.values(this.rebootsCancelTokens).forEach(s => s?.cancel?.("Component destroyed"))
    Object.values(this.ssdCancelTokens).forEach(s => s?.cancel?.("Component destroyed"))
  },
  methods: {
    // The two cards read their own query params, falling back to the shared
    // deviceIds so links that only name devices still fill both
    applyQuery() {
      const {
        deviceIds = "",
        start = "",
        end = "",
        ssdDeviceIds = "",
        ssdStart = "",
        ssdEnd = "",
        notes = "",
      } = this.$route.query

      const ids = this.parseIds(deviceIds)
      const ownSsdIds = this.parseIds(ssdDeviceIds)
      const ssdIds = ownSsdIds.length ? ownSsdIds : ids

      this.noteMarkers = this.parseNotesParam(notes)

      if (ids.length) {
        this.manualDeviceIds = [...ids]
      }
      if (start) this.manualStart = start
      if (end) this.manualEnd = end
      if (ssdStart) this.manualSsdStart = ssdStart
      if (ssdEnd) this.manualSsdEnd = ssdEnd

      this.applyDiagnosticsQuery(ids, start || this.manualStart, end || this.manualEnd)
      this.applySsdQuery(ssdIds, ssdStart || this.manualSsdStart, ssdEnd || this.manualSsdEnd)
    },
    parseIds(raw) {
      return raw ? raw.split(",").filter(Boolean) : []
    },
    // Only refetch when this card's own devices or range moved, so loading one
    // card doesn't re-run the other's query
    applyDiagnosticsQuery(ids, start, end) {
      if (!ids.length || !start || !end) return

      const key = `${ids.join(",")}|${start}|${end}`
      if (key === this.loadedDiagnosticsKey) return

      this.loadedDiagnosticsKey = key
      this.diagnosticsDeviceIds = ids
      this.diagnosticsStart = start
      this.diagnosticsEnd = end
      this.fetchDiagnostics()
    },
    applySsdQuery(ids, start, end) {
      if (!ids.length || !start || !end) return

      const key = `${ids.join(",")}|${start}|${end}`
      if (key === this.loadedSsdKey) return

      this.loadedSsdKey = key
      this.ssdDeviceIds = ids
      this.ssdStart = start
      this.ssdEnd = end
      this.fetchSsd()
    },
    parseNotesParam(raw) {
      if (!raw) return []

      return raw.split(";").flatMap(seg => {
        const [deviceId, list] = seg.split(":")
        if (!deviceId || !list) return []

        return list
          .split(",")
          .map(t => Number(t))
          .filter(Number.isFinite)
          .map(timestamp => ({ deviceId, timestamp }))
      })
    },
    fetchDiagnostics() {
      this.loading = true
      const tasks = this.diagnosticsDeviceIds.map(id => this.fetchMetrics(id))
      if (this.showReboots) {
        this.diagnosticsDeviceIds.forEach(id => tasks.push(this.fetchReboots(id)))
      }

      Promise.allSettled(tasks).finally(() => {
        this.loading = false
      })
    },
    fetchSsd() {
      this.ssdLoading = true

      Promise.allSettled(this.ssdDeviceIds.map(id => this.fetchSsdHealth(id))).finally(() => {
        this.ssdLoading = false
      })
    },
    formatRange(start, end) {
      return {
        startDate: dayjs.tz(start, "Pacific/Auckland").format("YYYY-MM-DD HH:mm:ss"),
        endDate: dayjs.tz(end, "Pacific/Auckland").format("YYYY-MM-DD HH:mm:ss"),
      }
    },
    fetchMetrics(deviceId) {
      this.metricsCancelTokens[deviceId]?.cancel?.("New fetch requested")
      const source = axios.CancelToken.source()
      this.metricsCancelTokens = { ...this.metricsCancelTokens, [deviceId]: source }

      const { startDate, endDate } = this.formatRange(this.diagnosticsStart, this.diagnosticsEnd)

      return store
        .dispatch("getDeviceEventsByType", {
          event_type: "diagnostics",
          start_datetime: startDate,
          end_datetime: endDate,
          device_id: deviceId,
          cancelToken: source.token,
        })
        .then(records => {
          const points = (records || [])
            .map(item => ({
              timestamp: dayjs.tz(item.event_data.date_time, "Pacific/Auckland").valueOf(),
              cpu_usage_percent: parseFloat(item.event_data.cpu_usage_percent),
              cpu_temp_celcius: parseFloat(item.event_data.cpu_temp_celcius),
              memory_usage_percent: parseFloat(item.event_data.memory_usage_percent),
              disk_usage_percent: parseFloat(item.event_data.disk_usage_percent),
            }))
            .sort((a, b) => a.timestamp - b.timestamp)
          this.$set(this.seriesByDevice, deviceId, points)
        })
        .catch(error => {
          if (!axios.isCancel(error)) {
            console.error(`Failed to load metrics for ${deviceId}:`, error)
            this.$set(this.seriesByDevice, deviceId, [])
          }
        })
    },
    fetchReboots(deviceId) {
      this.rebootsCancelTokens[deviceId]?.cancel?.("New fetch requested")
      const source = axios.CancelToken.source()
      this.rebootsCancelTokens = { ...this.rebootsCancelTokens, [deviceId]: source }

      const { startDate, endDate } = this.formatRange(this.diagnosticsStart, this.diagnosticsEnd)

      return store
        .dispatch("getDeviceEventsByType", {
          event_type: "reboots",
          start_datetime: startDate,
          end_datetime: endDate,
          device_id: deviceId,
          cancelToken: source.token,
        })
        .then(records => {
          this.$set(this.rebootsByDevice, deviceId, records || [])
        })
        .catch(error => {
          if (!axios.isCancel(error)) {
            console.error(`Failed to load reboots for ${deviceId}:`, error)
            this.$set(this.rebootsByDevice, deviceId, [])
          }
        })
    },
    fetchSsdHealth(deviceId) {
      this.ssdCancelTokens[deviceId]?.cancel?.("New fetch requested")
      const source = axios.CancelToken.source()
      this.ssdCancelTokens = { ...this.ssdCancelTokens, [deviceId]: source }

      const { startDate, endDate } = this.formatRange(this.ssdStart, this.ssdEnd)

      return store
        .dispatch("getDeviceEventsByType", {
          event_type: "ssd_health",
          start_datetime: startDate,
          end_datetime: endDate,
          device_id: deviceId,
          cancelToken: source.token,
        })
        .then(records => {
          this.$set(
            this.ssdByDevice,
            deviceId,
            (records || []).map(item => ({ type: item.event_type, ...item.event_data })),
          )
        })
        .catch(error => {
          if (!axios.isCancel(error)) {
            console.error(`Failed to load SSD health for ${deviceId}:`, error)
            this.$set(this.ssdByDevice, deviceId, [])
          }
        })
    },
    // Load keeps the URL shareable and applies straight away, so pressing it
    // with nothing changed still refetches rather than leaving the route static
    loadDiagnostics() {
      if (!this.canLoadDiagnostics) return

      this.pushQuery({
        deviceIds: this.manualDeviceIds.join(","),
        start: this.manualStart,
        end: this.manualEnd,
        notes: undefined,
      })
      this.noteMarkers = []
      this.loadedDiagnosticsKey = ""
      this.applyDiagnosticsQuery([...this.manualDeviceIds], this.manualStart, this.manualEnd)
    },
    loadSsd() {
      if (!this.canLoadSsd) return

      this.pushQuery({
        ssdDeviceIds: this.manualDeviceIds.join(","),
        ssdStart: this.manualSsdStart,
        ssdEnd: this.manualSsdEnd,
      })
      this.loadedSsdKey = ""
      this.applySsdQuery([...this.manualDeviceIds], this.manualSsdStart, this.manualSsdEnd)
    },
    pushQuery(params) {
      this.$router
        .replace({
          query: {
            ...this.$route.query,
            tab: "metrics",
            ...params,
          },
        })
        .catch(() => {})
    },
  },
}
</script>
